export interface TaskMonitorEvent {
  status: 'processing' | 'extracting menu' | 'completed' | 'failed'
  progress?: number
  percentCompleted?: number
  message?: string
  data?: any
}

export type TaskMonitorCallback = (event: TaskMonitorEvent) => void

export const monitorTask = (
  url: string,
  onMessage: TaskMonitorCallback,
  onError?: (error: Error) => void,
  reconnectInterval = 3000
): (() => void) => {
  let eventSource: EventSource | null = null
  let stopped = false
  let hasReceivedDone = false

  const closeConnection = () => {
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
  }

  const connect = () => {
    if (stopped) return

    eventSource = new EventSource(url)
    eventSource.addEventListener('connected', (event: Event) => {
      try {
        JSON.parse((event as MessageEvent).data)
      } catch (err) {
        onError?.(new Error('Invalid connected event payload'))
      }
    })

    // Handle 'inprogress' event for new API
    eventSource.addEventListener('inprogress', (event: Event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data)
        let status: TaskMonitorEvent['status'] = 'processing'

        // Map the API status to our internal status
        // Only mark as completed when API explicitly sends 'completed' status
        if (data.status === 'completed') {
          status = 'completed'
        } else if (data.status === 'failed') {
          status = 'failed'
        } else {
          // For 'in_progress' or any other status, keep as 'processing'
          status = 'processing'
        }

        onMessage({
          status,
          message: data.message || 'Processing...',
          percentCompleted: data.percentCompleted || 0,
          data
        })
      } catch (err) {
        onError?.(new Error('Invalid inprogress event payload'))
      }
    })

    // Keep 'progress' event for backwards compatibility
    eventSource.addEventListener('progress', (event: Event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data)
        const message = data.message?.toLowerCase()

        switch (message) {
          case 'processing':
            onMessage({ status: 'processing', message: data.message })
            break
          case 'extracting menu':
            onMessage({ status: 'extracting menu', message: data.message })
            break
          case 'completed':
            onMessage({ status: 'completed', message: data.message })
            break
          case 'failed':
            onMessage({ status: 'failed', message: data.message })
            break
          default:
            onMessage({
              status: 'processing',
              message: data.message || 'Processing'
            })
        }
      } catch (err) {
        onError?.(new Error('Invalid progress event payload'))
      }
    })

    eventSource.addEventListener('task-update', (event: Event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data)
        let status: TaskMonitorEvent['status'] = 'processing'

        if (data.lifecycle === 'Completed' || data.percentCompleted >= 100) {
          status = 'completed'
        } else if (data.lifecycle === 'Failed') {
          status = 'failed'
        }

        onMessage({
          status,
          message: data.message || 'Processing...',
          percentCompleted: data.percentCompleted || 0,
          data
        })
      } catch (err) {
        onError?.(new Error('Invalid task-update event payload'))
      }
    })

    eventSource.addEventListener('done', () => {
      hasReceivedDone = true
      closeConnection()
    })

    eventSource.onerror = () => {
      if (hasReceivedDone) {
        closeConnection()
        return
      }

      if (stopped) return

      closeConnection()
      onError?.(new Error('SSE connection error'))

      if (!stopped) {
        setTimeout(connect, reconnectInterval)
      }
    }
  }

  const cleanup = () => {
    stopped = true
    closeConnection()
  }
  connect()
  return cleanup
}
